import { doc, deleteDoc, getDoc, writeBatch, collection, getDocs, query, where, updateDoc } from 'firebase/firestore';
import { firestore } from '../../../lib/firebase/config';
import { PilgrimGuideState } from '../types';
import { COLLECTIONS } from '../constants';
import { useBansStore } from '../../bansStore';
import { useSuspensionStore } from '../../suspensionStore';

export const deleteLinkAction = async (
  linkId: string,
  set: (state: Partial<PilgrimGuideState>) => void,
  translations: any
) => {
  set({ loading: true, error: null });
  try {
    // Check for service suspension first
    const isSuspended = await useSuspensionStore
      .getState()
      .checkServiceSuspension('linking');
    
    if (isSuspended) {
      throw new Error('خدمة الربط معلقة حالياً');
    }

    // Get current user info
    const currentUserId = localStorage.getItem('userId');
    const userRole = localStorage.getItem('userRole');

    if (!currentUserId || !userRole) {
      throw new Error('You are not authorized to perform this action');
    }

    // Check for user ban
    const isUserBanned = await useBansStore
      .getState()
      .checkUserBans(currentUserId, 'linking');
    
    if (isUserBanned) {
      throw new Error('You are banned from using the linking service');
    }

    // Get link data
    const linkRef = doc(firestore, COLLECTIONS.LINKS, linkId);
    const linkSnap = await getDoc(linkRef);

    if (!linkSnap.exists()) {
      throw new Error(
        translations?.deleteLinkAction?.linkNotFound || 'Link not found'
      );
    }

    const linkData = linkSnap.data();

    // Check permissions
    if (userRole === 'guide' && linkData.guideId !== currentUserId) {
      throw new Error('You do not have permission to delete this link');
    }

    if (userRole === 'organizer' && linkData.organizerId !== currentUserId) {
      throw new Error('You do not have permission to delete this link');
    }

    // Only check accommodation if the link is not disputed
    if (linkData.status !== 'disputed') {
      // Check hotel accommodation
      const hotelTablesRef = collection(firestore, 'AccommodationTables');
      const hotelSnapshot = await getDocs(hotelTablesRef);
      
      for (const doc of hotelSnapshot.docs) {
        const table = doc.data();
        // Check if table.r exists and is an array before iterating
        if (table.r && Array.isArray(table.r)) {
          for (const room of table.r) {
            // Check if room.b exists and is an array before iterating
            if (room.b && Array.isArray(room.b)) {
              for (const bed of room.b) {
                // Check if bed.a exists and is an array before iterating
                if (bed.a && Array.isArray(bed.a)) {
                  // Check if pilgrim has any assignments in this bed
                  if (bed.a.some(assignment => assignment.pid === linkData.pilgrimId)) {
                    throw new Error(
                      translations?.deleteLinkAction?.hotelAccommodationError
                        ?.replace('{hotelName}', table.hn)
                        ?.replace('{roomNumber}', room.n) ||
                        `Cannot delete pilgrim as they are accommodated in hotel ${table.hn}, room ${room.n}`
                    );
                  }
                }
              }
            }
          }
        }
      }

      // Check camp accommodation
      const campTablesRef = collection(firestore, 'CampAccommodationTables');
      const campSnapshot = await getDocs(campTablesRef);

      for (const doc of campSnapshot.docs) {
        const table = doc.data();
        // Check if table.tents exists and is an array before iterating
        if (table.tents && Array.isArray(table.tents)) {
          for (const tent of table.tents) {
            // Check if tent.halls exists and is an array before iterating
            if (tent.halls && Array.isArray(tent.halls)) {
              for (const hall of tent.halls) {
                // Check if hall.beds exists and is an array before iterating
                if (hall.beds && Array.isArray(hall.beds)) {
                  for (const bed of hall.beds) {
                    if (bed.pilgrimId === linkData.pilgrimId) {
                      throw new Error(
                        translations?.deleteLinkAction?.campAccommodationError
                          ?.replace('{campName}', table.campName)
                          ?.replace('{tentNumber}', tent.tentNumber)
                          ?.replace('{hallNumber}', hall.hallNumber) ||
                          `Cannot delete pilgrim as they are accommodated in camp ${table.campName}, tent ${tent.tentNumber}, hall ${hall.hallNumber}`
                      );
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    const batch = writeBatch(firestore);

    // If this link is part of a dispute
    if (linkData.disputeId) {
      const disputeRef = doc(firestore, COLLECTIONS.DISPUTES, linkData.disputeId);
      const disputeSnap = await getDoc(disputeRef);

      if (disputeSnap.exists()) {
        const disputeData = disputeSnap.data();
        
        // Create ban for the withdrawing guide
        const banRef = doc(collection(firestore, COLLECTIONS.GUIDE_BANS));
        batch.set(banRef, {
          guideId: linkData.guideId,
          pilgrimId: linkData.pilgrimId,
          disputeId: linkData.disputeId,
          createdAt: Date.now()
        });

        // Remove this guide from the dispute
        const updatedGuides = disputeData.disputingGuides.filter(
          (g: any) => g.guideId !== linkData.guideId
        );

        // If only one guide remains, they win the dispute
        if (updatedGuides.length === 1) {
          const winningGuide = updatedGuides[0];

          // Find and update the winning guide's link
          const winningLinkQuery = query(
            collection(firestore, COLLECTIONS.LINKS),
            where('pilgrimId', '==', linkData.pilgrimId),
            where('guideId', '==', winningGuide.guideId)
          );
          const winningLinkSnap = await getDocs(winningLinkQuery);

          if (!winningLinkSnap.empty) {
            batch.update(winningLinkSnap.docs[0].ref, {
              status: 'approved',
              disputeId: null,
              updatedAt: Date.now()
            });
          }

          // Update ownership record
          const ownershipRef = collection(firestore, 'PilgrimOwnership');
          const ownershipQuery = query(
            ownershipRef,
            where('pilgrimId', '==', linkData.pilgrimId)
          );
          const ownershipSnapshot = await getDocs(ownershipQuery);

          if (!ownershipSnapshot.empty) {
            // Update existing ownership record
            batch.update(ownershipSnapshot.docs[0].ref, {
              realGuideId: winningGuide.guideId,
              ownerGuideId: winningGuide.guideId,
              updatedAt: Date.now()
            });
          } else {
            // Create new ownership record
            const newOwnershipRef = doc(collection(firestore, 'PilgrimOwnership'));
            batch.set(newOwnershipRef, {
              pilgrimId: linkData.pilgrimId,
              realGuideId: winningGuide.guideId,
              ownerGuideId: winningGuide.guideId,
              createdAt: Date.now(),
              updatedAt: Date.now()
            });
          }

          // Delete the dispute since it's resolved
          batch.delete(disputeRef);
        } else {
          // Update dispute with remaining guides
          batch.update(disputeRef, {
            disputingGuides: updatedGuides,
            updatedAt: Date.now()
          });
        }
      }
    }

    // Update PilgrimSelector document - Set guide and organizer related fields to null
    const selectorRef = collection(firestore, 'PilgrimSelector');
    const selectorQuery = query(selectorRef, where('pilgrimId', '==', linkData.pilgrimId));
    const selectorSnapshot = await getDocs(selectorQuery);
    
    if (!selectorSnapshot.empty) {
      const selectorDoc = selectorSnapshot.docs[0];
      const selectorData = selectorDoc.data();
      
      // If this link's guideId matches the selector's guideId, mark it inactive and reset guide fields
      if (selectorData.guideId === linkData.guideId) {
        batch.update(selectorDoc.ref, {
          status: 'inactive',
          guideId: null,
          guideName: null,
          organizerId: null,
          organizerName: null,
          'guideData.name': null,
          'guideData.organizerId': null,
          'organizerData.name': null,
          updatedAt: Date.now()
        });
      }
    }

    // Update operationlist - Set guide and organizer to null, preserve operator info
    const operationRef = collection(firestore, 'operationlist');
    const operationQuery = query(operationRef, 
      where('pilgrimId', '==', linkData.pilgrimId),
      where('guideId', '==', linkData.guideId)
    );
    const operationSnapshot = await getDocs(operationQuery);
    
    if (!operationSnapshot.empty) {
      operationSnapshot.docs.forEach(doc => {
        // Reset guide and organizer fields but PRESERVE operator fields
        batch.update(doc.ref, {
          guideId: null,
          guideName: null,
          organizerId: null,
          organizerName: null,
          updatedAt: Date.now()
        });
      });
    }

    // Delete the current link
    batch.delete(linkRef);

    // Commit all changes
    await batch.commit();

    // Update local state
    set((state) => {
      const newLinks = { ...state.links };
      delete newLinks[linkId];
      return {
        links: newLinks,
        selectedLinks: state.selectedLinks?.filter((id) => id !== linkId) || [],
        loading: false,
      };
    });
  } catch (error: any) {
    console.error('Error deleting link:', error);
    set({
      error:
        error.message ||
        translations?.deleteLinkAction?.deleteError ||
        'Failed to delete link',
      loading: false,
    });
    throw error;
  }
};